"use client";

import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useParams } from "next/navigation";
import { sendEventAnalytics } from "./eventAnalytics";
import {
  APP_KEYS,
  GET_PRODUCT_APPLICATION,
  PRODUCT_HANDLER,
} from "../constants";
import { useGetApplicationQuery, useGetCustomerQuery } from "../redux";
import { sessionStorageUtil } from "../utils/common.utils";

// Layout effects warn during SSR; fall back to useEffect on the server.
const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

const isDev = process.env.NEXT_PUBLIC_ENVIRONMENT !== "production";
const warn = (...args) => {
  if (isDev) console.warn("[useEvents]", ...args);
};

// Module-level, so "once" survives remounts and React Strict Mode double-invokes.
// Resets on full page reload unless `persist: "session"` is used.
const firedKeys = new Set();
const SESSION_KEY = APP_KEYS.HFC_EVENTS;
const EVENTS_ONCE_KEY = APP_KEYS.EVENTS_ONCE;

function hasFired(key, persist) {
  if (firedKeys.has(key)) return true;
  if (persist) {
    try {
      const eventsObj = sessionStorageUtil.get(SESSION_KEY, {});
      return eventsObj[EVENTS_ONCE_KEY]?.[key] === true;
    } catch (e) {
      return false;
    }
  }
  return false;
}

function markFired(key, persist) {
  firedKeys.add(key);
  if (persist) {
    try {
      const eventsObj = sessionStorageUtil.get(SESSION_KEY, {});
      eventsObj[EVENTS_ONCE_KEY] = eventsObj[EVENTS_ONCE_KEY] || {};
      eventsObj[EVENTS_ONCE_KEY][key] = true;
      sessionStorageUtil.set(SESSION_KEY, eventsObj);
    } catch (e) {}
  }
}

// RTK Query settled
const isSettled = (q) => !q.isUninitialized && !q.isLoading;

/**
 * Unified analytics hook with APPLICATION & CUSTOMER data.
 *
 * Returns:
 *  - sendEvent(eventName, extraPayload): send an event on demand (e.g. CTA click)
 *  - sendEventOnce(eventName, { key, skip, persist, ...extraPayload }):
 *      send an event at most once. If enrichment data is still loading, the
 *      event is queued and flushed once both queries settle (success or error).
 *      - key:     dedupe key (default: eventName)
 *      - skip:    don't send
 *      - persist: true to survive reloads within the tab session
 *
 * Usage:
 *   const { sendEvent, sendEventOnce } = useEvents();
 *   sendEventOnce("page_view")
 *   <button onClick={() => sendEvent("cta_click", { ctaName: "apply_now" })} />
 *
 *
 */
export function useEvents(sessionPersist = false) {
  const { product } = useParams();

  const applicationQuery = useGetApplicationQuery("");
  const customerQuery = useGetCustomerQuery();

  const dataReady = isSettled(applicationQuery) && isSettled(customerQuery);
  const applicationData = applicationQuery?.data;
  const customerData = customerQuery?.data;

  const latest = useRef({ applicationData, customerData, product, dataReady });

  // true while the component body is running, false once the commit is done
  const isRendering = useRef(false);
  isRendering.current = true;

  useIsomorphicLayoutEffect(() => {
    latest.current = { applicationData, customerData, product, dataReady };
    isRendering.current = false;
  });

  const pending = useRef(new Map());

  const sendEvent = useCallback((eventName, extraPayload = {}) => {
    if (!eventName) {
      warn("sendEvent called without an eventName");
      return;
    }

    try {
      const {
        applicationData: app,
        customerData: cust,
        product: prod,
      } = latest.current;

      const productApplication =
        GET_PRODUCT_APPLICATION(PRODUCT_HANDLER[prod], app?.data) || {};

      sendEventAnalytics({
        ...productApplication,
        ...cust?.data,
        ...extraPayload,
        eventName,
      });
    } catch (err) {
      warn(`failed to send "${eventName}"`, err);
    }
  }, []);

  const sendEventOnce = useCallback(
    (
      eventName,
      {
        skip = false,
        key = eventName,
        persist = sessionPersist,
        ...extraPayload
      } = {},
    ) => {
      if (!eventName) {
        warn("sendEventOnce called without an eventName");
        return;
      }
      if (skip || hasFired(key, persist) || pending.current.has(key)) return;

      // Called during render, or data not ready yet: queue it.
      if (isRendering.current || !latest.current.dataReady) {
        pending.current.set(key, { eventName, extraPayload, persist });
        return;
      }

      markFired(key, persist);
      sendEvent(eventName, extraPayload);
    },
    [sendEvent, sessionPersist],
  );

  // Runs after every commit (no dependency array on purpose).
  useEffect(() => {
    if (!dataReady || pending.current.size === 0) return;

    const queued = Array.from(pending.current.entries());
    pending.current.clear();

    queued.forEach(([key, { eventName, extraPayload, persist }]) => {
      if (hasFired(key, persist)) return;
      markFired(key, persist);
      sendEvent(eventName, extraPayload);
    });
  });

  return { sendEvent, sendEventOnce };
}
